import { EChartsOption } from 'echarts';
import { VoltageSpeedDashboardSample } from '../../voltage-with-speed.models';
import { getTimelinePoints } from '../timeline/timeline.utils';

interface SpeedVoltagePoint {
  speed: number;
  voltage: number;
  second: number;
}

export function getSpeedVoltagePoints(samples: VoltageSpeedDashboardSample[]): SpeedVoltagePoint[] {
  return getTimelinePoints(samples).flatMap(point => point.speed != null && point.speed > 0 && point.voltage != null
    ? [{ speed: point.speed, voltage: point.voltage, second: point.second }] : []);
}

export function getSpeedVoltageCorrelation(points: SpeedVoltagePoint[]): number | null {
  if (points.length < 2) return null;
  const reference = points[0];
  const meanSpeed = points.reduce((sum, point) => sum + point.speed - reference.speed, 0) / points.length;
  const meanVoltage = points.reduce((sum, point) => sum + point.voltage - reference.voltage, 0) / points.length;
  let covariance = 0;
  let speedVariance = 0;
  let voltageVariance = 0;
  for (const point of points) {
    const speed = point.speed - reference.speed - meanSpeed;
    const voltage = point.voltage - reference.voltage - meanVoltage;
    covariance += speed * voltage;
    speedVariance += speed * speed;
    voltageVariance += voltage * voltage;
  }
  if (speedVariance === 0 || voltageVariance === 0) return null;
  return Math.max(-1, Math.min(1, covariance / Math.sqrt(speedVariance * voltageVariance)));
}

export function buildSpeedVoltageScatterOptions(points: SpeedVoltagePoint[]): EChartsOption {
  const maxSpeed = points.reduce((max, point) => Math.max(max, point.speed), 0) * 1.07 || 100;
  const minVoltage = points.length ? points.reduce((min, point) => Math.min(min, point.voltage), Infinity) - 0.2 : 0;
  const maxVoltage = points.length ? points.reduce((max, point) => Math.max(max, point.voltage), -Infinity) + 0.2 : 1;
  return {
    animation: false,
    textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif' },
    aria: { enabled: true, description: 'رابطه سرعت و ولتاژ؛ هر نقطه یک نمونه معتبر هنگام حرکت است. محور افقی سرعت و محور عمودی ولتاژ است.' },
    grid: { left: 54, right: 14, top: 22, bottom: 75 },
    tooltip: {
      trigger: 'item', confine: true, renderMode: 'html',
      backgroundColor: '#06111ff2', borderColor: '#41627d', borderWidth: 1, padding: 11,
      extraCssText: 'min-width:170px;border-radius:10px;line-height:2;box-shadow:0 10px 20px #0008;direction:rtl;text-align:right;pointer-events:none;',
      textStyle: { color: '#edf5ff', fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif', fontSize: 13 },
      formatter: params => {
        const item = Array.isArray(params) ? params[0] : params;
        const point = points[item?.dataIndex];
        if (!point) return '';
        const time = new Date(point.second * 1000).toISOString().slice(11, 19);
        return `<b dir="ltr">${time}</b><br>سرعت: <span dir="ltr">${point.speed} km/h</span><br>ولتاژ: <span dir="ltr">${point.voltage.toFixed(2)} V</span>`;
      }
    },
    xAxis: {
      type: 'value', min: 0, max: maxSpeed, splitNumber: 4,
      name: 'سرعت (km/h)', nameLocation: 'end', nameGap: 0,
      nameTextStyle: { color: '#a3b9cc', fontSize: 10, align: 'right', verticalAlign: 'top', padding: [28, 0, 0, 0] },
      axisLabel: { color: '#93abc0', fontSize: 11, hideOverlap: true, formatter: (value: number) => String(Math.round(value)) },
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }
    },
    yAxis: {
      type: 'value', min: minVoltage, max: maxVoltage, splitNumber: 4,
      name: 'ولتاژ (V)', nameGap: 9, nameTextStyle: { color: '#a3b9cc', fontSize: 10, align: 'left' },
      axisLabel: { color: '#93abc0', fontSize: 11, formatter: (value: number) => value.toFixed(2) },
      axisLine: { show: false }, axisTick: { show: false },
      splitLine: { lineStyle: { color: '#294058', type: [4, 5], width: 1 } }
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, filterMode: 'none', start: 0, end: 100 },
      { type: 'inside', yAxisIndex: 0, filterMode: 'none', start: 0, end: 100 },
      {
        type: 'slider', xAxisIndex: 0, filterMode: 'none', start: 0, end: 100,
        left: 54, right: 14, bottom: 4, height: 18, showDetail: false,
        borderColor: '#263a50', backgroundColor: '#15283c', fillerColor: '#43a6ff22',
        handleStyle: { color: '#91a6bc', borderColor: '#41627d' },
        moveHandleStyle: { color: '#41627d' },
        showDataShadow: false
      }
    ],
    series: [{
      name: 'سرعت و ولتاژ', type: 'scatter', symbolSize: 7,
      itemStyle: { color: '#43a6ff', opacity: 0.48 },
      emphasis: { itemStyle: { opacity: 1, borderColor: '#edf5ff', borderWidth: 1 } },
      data: points.map(point => [point.speed, point.voltage])
    }]
  };
}
